/**
 * Turn a thrown value into something worth showing a user.
 *
 * Three kinds of message end up in catch blocks and they need different
 * treatment:
 *
 *   - Platform transport text ("A TLS error caused the secure connection to
 *     fail.", "Load failed") reads as a broken app when the real cause is a
 *     flaky moment on the network.
 *   - Server diagnostics ("No route for GET /v1/public/games/mega-millions",
 *     5xx text) describe our backend, not anything the user can act on.
 *   - Genuine user-facing copy — Clerk credential errors, 4xx validation
 *     messages like "Ticket image was unreadable" — should survive verbatim.
 *
 * The raw detail is never lost: the API client already reports it through the
 * `api_error` analytics event, which is where debugging should read it.
 */

const TRANSPORT_ERROR =
  /TLS|SSL|secure connection|network connection|Load failed|Failed to fetch|NetworkError|The Internet connection|timed out|timeout|offline/i;

const CONNECTION_COPY = "Couldn't reach Chancey. Check your connection and try again.";

interface ApiErrorShape {
  code?: string;
  status?: number;
}

interface ClerkErrorShape {
  errors?: Array<{ longMessage?: string; message?: string }>;
}

function clerkMessage(err: unknown): string | null {
  const first = (err as ClerkErrorShape)?.errors?.[0];
  return first?.longMessage || first?.message || null;
}

/**
 * @param fallback shown when the underlying detail is unusable or internal.
 *   Write it for the specific screen ("Could not load draw history.").
 */
export function friendlyErrorMessage(err: unknown, fallback: string): string {
  const fromClerk = clerkMessage(err);
  if (fromClerk) return fromClerk;

  const raw = err instanceof Error ? err.message : typeof err === 'string' ? err : '';
  if (!raw) return fallback;

  // Duck-typed rather than importing ApiError, which would make this module a
  // cycle for anything the API client itself wants to use.
  const api = err as ApiErrorShape;
  if (api?.code === 'network_error' || api?.code === 'timeout') return CONNECTION_COPY;
  if (TRANSPORT_ERROR.test(raw)) return CONNECTION_COPY;

  // Backend diagnostics: routing and 5xx text describe our infrastructure.
  const status = typeof api?.status === 'number' ? api.status : 0;
  if (status >= 500 || api?.code === 'not_found' || /^No route for /i.test(raw)) return fallback;

  return raw;
}

export const CONNECTION_ERROR_COPY = CONNECTION_COPY;
